import { useState } from 'react'
import { Link } from 'react-router-dom'
import { workbenchHref, subchainScope, type ContextWork, type ViewScope } from './context'
import OpenItemsSummary, { type WorkQueue } from './OpenItemsSummary'
const labels = { book: 'Book', evidence: 'Evidence', chase: 'Chase' }
/** Current structural work, never a fabricated close/sign-off percentage. */
export default function OpenItems({
  work,
  scope,
  showSummary = true,
}: {
  work: ContextWork
  scope: ViewScope
  showSummary?: boolean
}) {
  const [selected, setSelected] = useState<WorkQueue>('book')
  const current = work.queues.find((queue) => queue.name === selected)
  const discoverySupported = scope.kind !== 'subchain' && !scope.below_entity_id
  const discoveryIncomplete = work.book_discovery_complete === false
  return (
    <section className="fa-card nav-open-items" data-testid="context-open-items">
      <div className="fa-chd">Open items</div>
      {showSummary ? (
        <OpenItemsSummary work={work} selected={selected} onSelect={setSelected} />
      ) : null}
      <div className="nav-open-list">
        {work.cards
          .filter((card) => current?.card_ids.includes(card.id))
          .map((card) => (
            <div key={card.id} className="nav-open-row">
              <div>
                <strong>{card.label}</strong>
                <small>
                  {selected === 'book'
                    ? `${card.book_proposal_ids.length} proposed changes${card.removal_request_ids?.length ? ` · ${card.removal_request_ids.length} removal requests` : ''}`
                    : selected === 'evidence'
                      ? `${card.evidence_requirement_ids.length} requirements`
                      : `${card.chase_requirement_ids.length} requirements being chased`}
                </small>
              </div>
              <Link to={workbenchHref(subchainScope(card, scope), selected, card.id)}>
                Open {labels[selected]} ›
              </Link>
            </div>
          ))}
        {selected === 'book' && discoverySupported
          ? (work.book_cards ?? [])
              .filter((card) => current?.card_ids.includes(card.id))
              .map((card) => (
                <div key={card.id} className="nav-open-row">
                  <div>
                    <strong>{card.label}</strong>
                    <small>{card.proposal_ids.length} proposed changes</small>
                  </div>
                  <Link to={workbenchHref(scope, 'book', card.id)}>Open Book ›</Link>
                </div>
              ))
          : null}
        {selected === 'book' && discoveryIncomplete ? (
          <p role="status">Some Book items could not be loaded. The list may be incomplete.</p>
        ) : null}
        {selected === 'book'
          ? (work.removal_requests ?? [])
              .filter(
                (request) =>
                  !work.cards.some((card) => card.removal_request_ids?.includes(request.id))
              )
              .map((request) => (
                <div key={request.id} className="nav-open-row">
                  <div>
                    <strong>{request.label ?? 'Removal request'}</strong>
                    <small>Manager review</small>
                  </div>
                  <Link to={workbenchHref(scope, 'book')}>Review removal ›</Link>
                </div>
              ))
          : null}
        {current?.counts.card_count === 0 &&
        !(selected === 'book' && (work.removal_requests?.length || discoveryIncomplete)) ? (
          <p className="nav-muted">
            No current {labels[selected].toLowerCase()} items in this context.
          </p>
        ) : null}
        {work.unassigned_work.length ? (
          <p>
            <Link to="/v2/traces">{work.unassigned_work.length} items need context ›</Link>
          </p>
        ) : null}
      </div>
    </section>
  )
}
